import { create } from "zustand";
import axios from "axios";

const config = window.upsellConfig || {};
const formattedString = window.formattedString || "$";
const orderId = window.orderId || "";
const postId = window.postId || null;

const processBundleProducts = (products = []) => {
  return products.map((p) => ({
    product_image: p.product_image || "",
    product_name: p.product_name || "",
    konnektive_id: p.konnektive_id || "",
    sku: p.sku || "",
    price: parseFloat(p.price) || 0,
    old_price: parseFloat(p.old_price) || 0,
    amount: parseInt(p.amount) || 1,
    primary_product: Boolean(p.primary_product),
  }));
};

const buildRedirectUrl = (url, orderId) => {
  if (!url) return "";
  const separator = url.includes("?") ? "&" : "?";
  return orderId ? `${url}${separator}orderId=${orderId}` : url;
};

const formatPrice = (price) => {
  return formattedString.replace(/[\d.,]+/, price.toFixed(2));
};

const useUpsellStore = create((set, get) => ({
  links: config.upsell_links || {},
  currencyCode: config.currency_code || "USD",
  bundleProducts: processBundleProducts(config.bundle_products),
  shippingThreshold: parseFloat(config.shipping_threshold) || 0,
  orderId: orderId,
  postId: postId,
  isModalOpen: true,
  isSubmitting: false,

  getPrimaryProduct: () => {
    const { bundleProducts } = get();
    return bundleProducts.find((p) => p.primary_product) || bundleProducts[0] || null;
  },

  getFormattedTotalPrice: () => {
    const primaryProduct = get().getPrimaryProduct();
    if (!primaryProduct) return "";
    const total = primaryProduct.price * primaryProduct.amount;
    return formatPrice(total);
  },

  getFormattedPrimaryOldPrice: () => {
    const primaryProduct = get().getPrimaryProduct();
    if (!primaryProduct) return "";
    const oldPrice = primaryProduct.old_price || 0;
    return oldPrice > 0 ? formatPrice(oldPrice) : "";
  },

  getFormattedFreeProductsValue: () => {
    const { bundleProducts } = get();
    const freeProductsValue = bundleProducts
      .filter((p) => p.price === 0)
      .reduce((total, product) => total + (product.old_price || 0), 0);

    return freeProductsValue > 0 ? formatPrice(freeProductsValue) : "";
  },

  getFormattedSavings: () => {
    const primaryProduct = get().getPrimaryProduct();
    if (!primaryProduct) return "";

    const currentTotal = primaryProduct.price * primaryProduct.amount;
    const oldTotal = primaryProduct.old_price || 0;
    const savings = oldTotal - currentTotal;

    return savings > 0 ? formatPrice(savings) : "";
  },

  getFormattedShippingThreshold: () => {
    const { shippingThreshold } = get();
    // Format without decimals if it's a whole number
    const formatted = Number.isInteger(shippingThreshold)
      ? shippingThreshold.toString()
      : shippingThreshold.toFixed(2);
    return formattedString.replace(/[\d.,]+/, formatted);
  },

  openModal: () => set({ isModalOpen: true }),

  closeModal: () => {
    const { links, orderId } = get();
    set({ isModalOpen: false });
    const declineUrl = buildRedirectUrl(links.link_decline, orderId);
    if (declineUrl) {
      window.location.href = declineUrl;
    }
  },

  postUpsellData: async () => {
    const state = get();
    const { bundleProducts, orderId, postId, links } = state;
    const primaryProduct = state.getPrimaryProduct();
    const primaryPrice = primaryProduct?.price || 0;
    const primaryQuantity = primaryProduct?.amount || 1;
    const primaryTotalAmount = primaryPrice * primaryQuantity;

    set({ isSubmitting: true });

    const requestData = {
      orderId: orderId,
      postId: postId,
      upsell: 1,
      sku: primaryProduct?.sku || "",
      konnektive_id: primaryProduct?.konnektive_id || "",
      upsellType: "upsell",
      bundle_products: bundleProducts.map((p) => ({
        bp_konnektive_id: p.konnektive_id,
        bp_sku: p.sku,
        bp_price: p.price,
        bp_amount: p.amount,
        bp_old_price: p.old_price || 0,
        primary_product: p.primary_product,
      })),
      price: primaryPrice,
      productOldPrice: primaryProduct?.old_price || 0,
      quantity: primaryQuantity,
      totalAmount: primaryTotalAmount,
      variants: null,
      title: primaryProduct?.product_name || "",
      checkoutWarrantyState: false,
      hasWarranty: false,
      warrantyState: false,
    };

    try {
      const response = await axios.post(
        "/wp-json/checkout/v1/order/upsell",
        requestData
      );

      if (response.data.result === "SUCCESS") {
        window.location.href = buildRedirectUrl(links.link_success, orderId);
      } else {
        window.location.href = buildRedirectUrl(links.link_failure, orderId);
      }
    } catch (error) {
      console.error("Upsell API error:", error);
      set({ isSubmitting: false });
      window.location.href = buildRedirectUrl(links.link_failure, orderId);
    }
  },
}));

export default useUpsellStore;
